import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang, localizedField } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, BedDouble, Bath, Maximize, MapPin, Wifi, KeyRound, Headphones, Coffee, Briefcase, Check, Star, MapPinned, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";

const AMENITY_ICONS = {
  wifi: { icon: Wifi, key: "amenity_wifi" },
  kitchen: { icon: Coffee, key: "amenity_kitchen" },
  self_checkin: { icon: KeyRound, key: "amenity_self_checkin" },
  support: { icon: Headphones, key: "amenity_support" },
  coffee: { icon: Coffee, key: "amenity_coffee" },
  workspace: { icon: Briefcase, key: "amenity_workspace" },
};

export default function ApartmentDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [apt, setApt] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [form, setForm] = useState({ check_in: "", check_out: "", guests: 2, guest_name: "", guest_email: "", guest_phone: "" });
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], average: null, count: 0 });
  const [reviewForm, setReviewForm] = useState({ guest_name: "", rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [promo, setPromo] = useState(0);

  useEffect(() => {
    api.get(`/apartments/${id}`).then((r) => setApt(r.data)).catch(() => navigate("/apartments"));
    api.get(`/reviews/${id}`).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/promo").then((r) => setPromo(Number(r.data.percent) || 0)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    if (!apt) return;
    const name = localizedField(apt, "name", lang);
    document.title = `${name} – Atlas Apartment`;

    // Meta-Description pro Wohnung setzen statt einer einzigen generischen Beschreibung für
    // alle Seiten - wichtig für Google-Suchergebnisse und Vorschauen beim Teilen des Links.
    const desc = localizedField(apt, "description", lang) ||
      `${name} in ${apt.city} – ${apt.max_guests} Gäste, ${apt.size_sqm} m². Direkt buchen zum Bestpreis.`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", desc.slice(0, 160));

    // Canonical-Link auf die aktuelle Wohnung, sonst würde Google evtl. denken, alle
    // Wohnungsseiten seien Duplikate der Startseite (dort steht ein fester canonical-Tag).
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://atlas-apartment.de/apartment/${apt.id}`);

    return () => {
      // Beim Verlassen zurück auf die generischen Website-Defaults setzen.
      metaDesc.setAttribute("content", "Atlas Apartment GbR – Stilvolle Serviced Apartments in Oberhausen und Dinslaken. Direkt buchen, bester Preis.");
      canonical.setAttribute("href", "https://atlas-apartment.de/");
    };
  }, [apt, lang]);

  // Strukturierte Daten (schema.org) pro Wohnung: hilft Google, in den Suchergebnissen
  // direkt Preis, Fotos und Bewertung anzuzeigen (Rich Snippets), statt nur einen blauen Link.
  useEffect(() => {
    if (!apt) return;
    const name = localizedField(apt, "name", lang);
    const description = localizedField(apt, "description", lang) ||
      `${name} in ${apt.city} – ${apt.max_guests} Gäste, ${apt.size_sqm} m².`;

    const data = {
      "@context": "https://schema.org",
      "@type": "Apartment",
      "name": name,
      "description": description,
      "url": `https://atlas-apartment.de/apartment/${apt.id}`,
      "image": apt.images || [],
      "numberOfRooms": apt.bedrooms,
      "petsAllowed": false,
      "occupancy": { "@type": "QuantitativeValue", "maxValue": apt.max_guests },
      "floorSize": { "@type": "QuantitativeValue", "value": apt.size_sqm, "unitCode": "MTK" },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": apt.city,
        "addressRegion": "Nordrhein-Westfalen",
        "addressCountry": "DE",
      },
      "offers": {
        "@type": "Offer",
        "price": apt.price_per_night,
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "url": `https://atlas-apartment.de/apartment/${apt.id}`,
      },
    };
    if (reviews?.count > 0 && reviews.average) {
      data.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": reviews.average,
        "reviewCount": reviews.count,
      };
    }

    let script = document.getElementById("apartment-schema");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "apartment-schema";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => { script?.remove(); };
  }, [apt, reviews, lang]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox || !apt) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") setActive(p => (p + 1) % apt.images.length);
      if (e.key === "ArrowLeft") setActive(p => (p - 1 + apt.images.length) % apt.images.length);
      if (e.key === "Escape") setLightbox(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, apt]);

  const submitReview = async () => {
    if (!reviewForm.guest_name || !reviewForm.rating) return toast.error(t("your_details"));
    setSubmittingReview(true);
    try {
      await api.post(`/reviews/${id}`, reviewForm);
      toast.success(t("review_thanks"));
      setReviewForm({ guest_name: "", rating: 5, comment: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSubmittingReview(false); }
  };

  if (!apt) return (
    <div className="pt-40 text-center">
      <div className="inline-block w-8 h-8 border-2 border-[#C05C3B] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const today = new Date().toISOString().split("T")[0];
  const nights = form.check_in && form.check_out
    ? Math.max(0, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000)) : 0;
  const rawTotal = nights * apt.price_per_night;
  const total = (promo > 0 ? Math.round(rawTotal * (100 - promo)) / 100 : rawTotal).toFixed(2);
  const perNight = promo > 0 ? (Math.round(apt.price_per_night * (100 - promo)) / 100).toFixed(2) : apt.price_per_night;

  const submit = async () => {
    if (!form.check_in || !form.check_out || nights < 1) return toast.error(t("select_dates"));
    if (!form.guest_name || !form.guest_email) return toast.error(t("your_details"));
    setLoading(true);
    try {
      const avail = await api.get(`/availability/${id}`, { params: { arrival: form.check_in, departure: form.check_out } });
      if (!avail.data.available) { toast.error(t("not_available")); setLoading(false); return; }
      const res = await api.post("/checkout/session", { apartment_id: id, ...form, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 pb-24 min-h-screen">
      {/* Back button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <button onClick={() => navigate("/apartments")}
          className="flex items-center gap-2 text-sm text-[#5C6656] hover:text-[#C05C3B] transition-colors">
          <ArrowLeft size={15} /> {t("all_apartments_btn")}
        </button>
      </div>

      {/* Photo gallery */}
      {/* Mobile gallery */}
      <div className="md:hidden max-w-7xl mx-auto px-4 mb-6">
        <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
          <img src={apt.images[active]} alt={`${localizedField(apt, "name", lang)} – Foto ${active + 1}`} width="800" height="600" className="w-full h-full object-cover"
            onClick={() => setLightbox(true)} />
          {apt.images.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 flex gap-1.5 p-3 overflow-x-auto">
              {apt.images.map((src, i) => (
                <button key={i} onClick={() => setActive(i)} aria-label={`Foto ${i + 1} anzeigen`}
                  className={`shrink-0 w-12 h-12 rounded-sm overflow-hidden border-2 transition-all ${active === i ? "border-[#C05C3B]" : "border-white/50"}`}>
                  <img src={src} alt={`${localizedField(apt, "name", lang)} – Miniaturbild ${i + 1}`} width="48" height="48" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Desktop gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        {apt.images.length > 0 ? (
          <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[65vh]">
            <div className="col-span-2 row-span-2 overflow-hidden rounded-sm cursor-pointer relative group"
              onClick={() => setLightbox(true)}>
              <img src={apt.images[0]} alt={`${localizedField(apt, "name", lang)} in ${apt.city}`} width="800" height="900" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              {reviews.average && (
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                  <Star size={13} className="fill-[#C05C3B] text-[#C05C3B]" />
                  <span className="text-sm font-bold text-[#2A3324]">{reviews.average}</span>
                  <span className="text-xs text-[#5C6656]">({reviews.count})</span>
                </div>
              )}
            </div>
            {apt.images.slice(1, 5).map((src, i) => (
              <div key={i} className="overflow-hidden rounded-sm cursor-pointer relative group"
                onClick={() => { setActive(i + 1); setLightbox(true); }}>
                <img src={src} alt={`${localizedField(apt, "name", lang)} – Foto ${i + 2}`} width="400" height="440" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {i === 3 && apt.images.length > 5 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white font-bold text-xl">+{apt.images.length - 5}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="h-[50vh] bg-[#F0EBE1] rounded-sm flex items-center justify-center text-[#5C6656]">
            {t("no_photos")}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} aria-label="Schließen" className="absolute top-6 right-6 text-white/70 hover:text-white text-3xl">✕</button>
          <button onClick={(e) => { e.stopPropagation(); setActive(p => (p - 1 + apt.images.length) % apt.images.length); }}
            aria-label="Vorheriges Foto"
            className="absolute left-4 text-white/70 hover:text-white p-3 bg-white/10 rounded-full">
            <ChevronLeft size={24} />
          </button>
          <img src={apt.images[active]} alt={`${localizedField(apt, "name", lang)} – Foto ${active + 1} von ${apt.images.length}`} width="1200" height="900" className="max-h-[90vh] max-w-[90vw] object-contain" onClick={e => e.stopPropagation()} />
          <button onClick={(e) => { e.stopPropagation(); setActive(p => (p + 1) % apt.images.length); }}
            aria-label="Nächstes Foto"
            className="absolute right-4 text-white/70 hover:text-white p-3 bg-white/10 rounded-full">
            <ChevronRight size={24} />
          </button>
          <div className="absolute bottom-6 text-white/50 text-sm">{active + 1} / {apt.images.length}</div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 text-[#C05C3B] text-xs uppercase tracking-widest mb-3">
              <MapPin size={13} /> {apt.city} · {apt.neighborhood}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold mb-4 leading-tight">
              {localizedField(apt, "name", lang)}
            </h1>
            <div className="flex flex-wrap gap-6 text-[#5C6656] text-sm mb-8 pb-8 border-b border-[#E5E0D8]">
              <span className="flex items-center gap-2"><Users size={16} /> {t("up_to")} {apt.max_guests} {t("guests")}</span>
              <span className="flex items-center gap-2"><BedDouble size={16} /> {apt.bedrooms} {t("bedrooms")}</span>
              <span className="flex items-center gap-2"><Bath size={16} /> {apt.bathrooms} {t("bathrooms")}</span>
              <span className="flex items-center gap-2"><Maximize size={16} /> {apt.size_sqm} m²</span>
            </div>

            <p className="text-lg text-[#2A3324] leading-relaxed mb-10">
              {localizedField(apt, "description", lang)}
            </p>

            {/* Amenities */}
            <h3 className="font-display text-2xl font-semibold mb-5">{t("amenities")}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12">
              {apt.amenities.map((a) => {
                const meta = AMENITY_ICONS[a];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div key={a} className="flex items-center gap-3 bg-[#F0EBE1] px-4 py-3 rounded-sm">
                    <Icon size={16} className="text-[#C05C3B] shrink-0" />
                    <span className="text-sm font-medium text-[#2A3324]">{t(meta.key)}</span>
                  </div>
                );
              })}
            </div>

            {/* Map */}
            <h3 className="font-display text-2xl font-semibold mb-5 flex items-center gap-2">
              <MapPinned size={20} className="text-[#C05C3B]" /> {t("location_label")}
            </h3>
            <div className="overflow-hidden rounded-sm border border-[#E5E0D8] aspect-[16/9] sm:aspect-[16/7] mb-12">
              <iframe title="map" className="w-full h-full" loading="lazy"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(`${apt.neighborhood} ${apt.city} Germany`)}&output=embed`} />
            </div>

            {/* Reviews */}
            <div>
              <h3 className="font-display text-2xl font-semibold mb-6 flex items-center gap-2">
                <Star size={20} className="text-[#C05C3B]" /> {t("reviews_label")}
                {reviews.average && (
                  <span className="text-base font-normal text-[#5C6656]">
                    · {reviews.average} ({reviews.count})
                  </span>
                )}
              </h3>
              {reviews.reviews.length === 0 && (
                <p className="text-[#5C6656] text-sm mb-6">
                  {t("no_reviews_yet")}
                </p>
              )}
              <div className="space-y-4 mb-8">
                {reviews.reviews.map((r) => (
                  <div key={r.id} className="bg-white border border-[#E5E0D8] rounded-sm p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#2A3324] text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {r.guest_name[0]}
                        </div>
                        <span className="font-semibold text-sm">{r.guest_name}</span>
                      </div>
                      <div className="flex">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star key={i} size={12} className={i < r.rating ? "fill-[#C05C3B] text-[#C05C3B]" : "text-[#E5E0D8]"} />
                        ))}
                      </div>
                    </div>
                    {r.comment && <p className="text-sm text-[#5C6656] leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>

              {/* Review form */}
              <div className="bg-[#F0EBE1] rounded-sm p-6">
                <h4 className="font-semibold mb-4">{t("leave_review")}</h4>
                <input placeholder={t("full_name")} value={reviewForm.guest_name}
                  onChange={(e) => setReviewForm({ ...reviewForm, guest_name: e.target.value })}
                  className="w-full bg-white border border-[#E5E0D8] rounded-sm px-4 py-2.5 text-sm mb-3 outline-none focus:ring-2 focus:ring-[#C05C3B]" />
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <button key={i} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: i + 1 })}
                      aria-label={`${i + 1} ${i === 0 ? "Stern" : "Sterne"}`} aria-pressed={i < reviewForm.rating}>
                      <Star size={24} className={i < reviewForm.rating ? "fill-[#C05C3B] text-[#C05C3B]" : "text-[#E5E0D8]"} />
                    </button>
                  ))}
                </div>
                <textarea placeholder={t("your_comment_ph")}
                  value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} rows={3}
                  className="w-full bg-white border border-[#E5E0D8] rounded-sm px-4 py-2.5 text-sm mb-3 outline-none focus:ring-2 focus:ring-[#C05C3B]" />
                <button onClick={submitReview} disabled={submittingReview}
                  className="px-6 py-2.5 bg-[#2A3324] hover:bg-[#1f261b] disabled:opacity-60 text-white text-sm font-semibold rounded-sm transition-colors">
                  {submittingReview ? "…" : t("submit_btn")}
                </button>
              </div>
            </div>
          </div>

          {/* Booking card */}
          <div>
            <div className="bg-white border border-[#E5E0D8] rounded-sm shadow-[0_4px_20px_rgba(42,51,36,0.10)] overflow-hidden mt-8 lg:mt-0">
              <div className="bg-[#2A3324] p-6 text-white">
                <div className="flex items-baseline gap-1">
                  {promo > 0 && <span className="text-white/50 line-through text-xl mr-1">€{apt.price_per_night}</span>}
                  <span className="font-display text-4xl font-semibold">€{perNight}</span>
                  {promo > 0 && <span className="ml-2 text-xs font-semibold bg-[#D9B473] text-[#13160F] px-2 py-0.5 rounded-full">−{promo}%</span>}
                  <span className="text-white/60 text-sm">{t("per_night")}</span>
                </div>
                {reviews.average && (
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={12} className="fill-[#E8A87C] text-[#E8A87C]" />
                    <span className="text-sm text-white/80">{reviews.average} · {reviews.count} {t("reviews_word")}</span>
                  </div>
                )}
              </div>

              <div className="p-6 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C6656] mb-1 block">{t("search_checkin")}</label>
                    <input type="date" min={today} value={form.check_in}
                      onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                      className="w-full border border-[#E5E0D8] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C05C3B]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C6656] mb-1 block">{t("search_checkout")}</label>
                    <input type="date" min={form.check_in || today} value={form.check_out}
                      onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                      className="w-full border border-[#E5E0D8] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C05C3B]" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C6656] mb-1 block">{t("search_guests")}</label>
                  <select value={form.guests} onChange={(e) => setForm({ ...form, guests: Number(e.target.value) })}
                    className="w-full border border-[#E5E0D8] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C05C3B]">
                    {Array.from({ length: apt.max_guests }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} {n === 1 ? t("guest") : t("guests")}</option>
                    ))}
                  </select>
                </div>
                <input placeholder={t("full_name")} value={form.guest_name}
                  onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  className="w-full border border-[#E5E0D8] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C05C3B]" />
                <input type="email" placeholder={t("email")} value={form.guest_email}
                  onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  className="w-full border border-[#E5E0D8] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C05C3B]" />
                <input placeholder={t("phone")} value={form.guest_phone}
                  onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                  className="w-full border border-[#E5E0D8] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C05C3B]" />

                {nights > 0 && (
                  <div className="bg-[#F0EBE1] rounded-sm p-4 space-y-2 text-sm">
                    <div className="flex justify-between text-[#5C6656]">
                      <span>€{apt.price_per_night} × {nights} {t("nights")}</span>
                      <span>€{rawTotal.toFixed(2)}</span>
                    </div>
                    {promo > 0 && (
                      <div className="flex justify-between text-[#3B6E4F] font-medium">
                        <span>Aktion −{promo}%</span>
                        <span>−€{(rawTotal - Number(total)).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-[#2A3324] text-base pt-2 border-t border-[#E5E0D8]">
                      <span>{t("total")}</span>
                      <span>€{total}</span>
                    </div>
                  </div>
                )}

                <button onClick={submit} disabled={loading}
                  className="w-full py-4 bg-[#C05C3B] hover:bg-[#A64D30] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-widest rounded-sm transition-colors">
                  {loading ? t("processing") : t("proceed_payment")}
                </button>

                <div className="flex items-center justify-center gap-4 text-xs text-[#5C6656] pt-1">
                  <span className="flex items-center gap-1"><Check size={12} className="text-[#3B6E4F]" /> Bestpreis</span>
                  <span className="flex items-center gap-1"><Check size={12} className="text-[#3B6E4F]" /> Stripe</span>
                  <span className="flex items-center gap-1"><Check size={12} className="text-[#3B6E4F]" /> Sicher</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
